import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  Card,
  createStyles,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  Select,
  Tabs,
} from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { GET_REGIONS, GET_RETAILERS } from "apollo/queries";
import ShowWarehouseLocation from "components/Warehouse/showWarehouseLocation";
import DateFilter from "components/controls/DateFilter";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";
import * as XLSX from "xlsx";
import Suppliers from "./suppliers";
const columns = [
  { header: "Name", dataKey: "name" },
  { header: "Email", dataKey: "contact_email" },
  { header: "Phone", dataKey: "contact_phone" },
  { header: "City", dataKey: "city" },
  { header: "Region", dataKey: "region.name.en" },
];
const useStyles = createStyles((theme) => ({
  root: {
    padding: theme.spacing.xl * 1.5,
  },
  value: {
    fontSize: 17,
    fontWeight: 500,
    lineHeight: 1,
  },
  diff: {
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
  },
}));

function SalesDetailModal({ sales }) {
  const { classes } = useStyles();
  const [productLoading, setProductLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [regionsDropDownData, setRegionsDropDownData] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState("10");
  const [openedLocation, setOpenedLocation] = useState(false);
  const [activePage, setActivePage] = useState(1);

  const [location, setLocation] = useState({});
  const { data, loading, refetch } = useQuery(GET_RETAILERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      period: timeRange,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      region_id: selectedRegion ? String(selectedRegion) : null,
      sales_id: String(sales.id),
      search: "",
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get retailers ",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.retailers.paginatorInfo.lastPage);
    }
  }, [data, size]);

  const { loading: regionsLoading } = useQuery(GET_REGIONS, {
    variables: {
      first: 100000,
      page: 1,
    },
    onCompleted(data) {
      let regions = data.regions;
      let regionsArray = [];
      regions.data.forEach((region) => {
        regionsArray.push({
          label: region?.name,
          value: region?.id,
        });
      });
      setRegionsDropDownData([...regionsArray]);
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: `${err}`,
      });
    },
  });

  const { height } = useViewportSize();

  const setRegionDropDownValue = (val) => {
    setSelectedRegion(val);
  };
  useEffect(() => {
    refetch();
  }, []);
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth(); // Get PDF width

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${sales?.user.name} Sales Person Report`, pageWidth / 2, 20, {
      align: "center",
    });

    const headers = columns.map((col) => col.header);
    const bodyData = data?.retailers?.data?.map((row) => [
      row.name || "N/A",
      row.contact_email || "N/A",
      row.contact_phone || "N/A",
      row.city || "N/A",
      row.region?.name || "N/A",
    ]);

    doc.autoTable({
      startY: 35,
      head: [headers],
      body: bodyData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [255, 106, 0], textColor: [255, 255, 255] }, // Updated header color
    });
    const pageHeight = doc.internal.pageSize.getHeight();

    const exportedDate = `Exported Date: ${new Date().toLocaleString()}`;

    doc.setFontSize(10);
    doc.text(exportedDate, pageWidth - 14, pageHeight - 10, { align: "right" });

    doc.save(`${sales?.user.name}_sales_person_report.pdf`);
  };

  const exportToExcel = () => {
    const bodyData = data?.retailers?.data.map((row) => ({
      Name: row.name,
      Email: row.contact_email,
      Phone: row.contact_phone,
      City: row.city,
      Region: row.region.name,
    }));

    const worksheet = XLSX.utils.json_to_sheet(bodyData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales_retailers");
    XLSX.writeFile(workbook, `${sales?.user.name}_Sales_retailers.xlsx`);
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "City",
      key: "city",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.city}</span>;
      },
    },
    {
      label: "Phone",
      key: "contact_phone",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.contact_phone}</span>;
      },
    },
    {
      label: "Region",
      key: "city",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.region?.name}</span>;
      },
    },
    {
      label: "Orders",
      key: "orderCount",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.orderCount}</span>;
      },
    },
    {
      label: "Balance",
      key: "balance",
      sortable: false,
      searchable: true,
      exportable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.wallet ? formatNumber(rowData.wallet.balance) : 0}
          </span>
        );
      },
    },
    {
      label: "Date",
      key: "created_at",
      sortable: true,
      searchable: false,
      exportable: false,
      render: (rowData) => <span>{rowData.created_at.split(" ")[0]}</span>,
    },
  ];
  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };
  const [activeTab, setActiveTab] = useState("first");
  const handleTabChange = async (tab) => {
    console.log(tab);
    setActiveTab(tab);
  };
  const [fetchAllRetailers] = useLazyQuery(GET_RETAILERS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllRetailers({
      variables: {
        first: null,
        page: 1,
        period: timeRange,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        region_id: selectedRegion ? String(selectedRegion) : null,
        sales_id: String(sales.id),
      },
    });
    return allData?.retailers?.data || [];
  };
  return (
    <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
      <div style={{ width: "98%", margin: "auto" }}>
        <LoadingOverlay
          visible={productLoading || regionsLoading}
          color="blue"
          overlayBlur={2}
          loader={customLoader}
        />
        <Tabs
          color="#FF6A00"
          value={activeTab}
          styles={{
            tabList: {
              borderBottom: `2px solid #FF6A00`,
            },
            tab: {
              "&[data-active]": {
                borderColor: "#FF6A00",
              },
              "&:hover": {
                borderColor: "#FF6A00",
              },
            },
          }}
          onTabChange={handleTabChange}
        >
          <Tabs.List>
            <Tabs.Tab value="first">
              <span style={{ color: "#666666", fontWeight: "bold" }}>
                Retailers
              </span>
            </Tabs.Tab>

            <Tabs.Tab value="two">
              <span style={{ color: "#666666", fontWeight: "bold" }}>
                Suppliers
              </span>
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="two">
            <Suppliers sales={sales} />
          </Tabs.Panel>
          <Tabs.Panel value="first">
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <DateFilter
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                selectedStartDate={selectedStartDate}
                onStartDateChange={setSelectedStartDate}
                selectedEndDate={selectedEndDate}
                onEndDateChange={setSelectedEndDate}
              />
              <Select
                data={regionsDropDownData}
                searchable
                clearable
                onChange={setRegionDropDownValue}
                placeholder="Pick a region to filter retailers"
                style={{ width: "240px" }}
              />
              <ExportComponent
                data={data ? data.retailers.data : []}
                headers={headerData}
                onFetch={handleExportDataFetch}
                reportTitle={`${sales?.user.name} Sales Person Report`}
                fileNamePrefix={`${sales?.user.name}_sales_person_report`}
              />
            </Box>
            <Drawer
              opened={openedLocation}
              onClose={() => setOpenedLocation(false)}
              title="Warehouse Location"
              padding="xl"
              size="80%"
              position="bottom"
            >
              <ShowWarehouseLocation location={location} />
            </Drawer>
            <Card style={{ marginTop: "5px" }} shadow="sm" p="lg">
              <ScrollArea>
                <B2bTable
                  searchInput={false}
                  total={total}
                  paginatorInfo={data.retailers.paginatorInfo}
                  activePage={activePage}
                  handleChange={handleChange}
                  header={headerData}
                  loading={loading}
                  data={data ? data.retailers.data : []}
                  size={size}
                  handlePageSizeChange={handlePageSizeChange}
                />
              </ScrollArea>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  flexDirection: "column",
                  p: 1,
                  gap: 1,
                  m: 1,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              >
                <div
                  style={{
                    marginRight: "35px",
                    fontWeight: "bold",
                    fontSize: "15px",
                  }}
                >
                  Total Retailers:{" "}
                  {formatNumber(data?.retailers.paginatorInfo.total)}
                </div>
              </Box>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </div>
    </ScrollArea>
  );
}

export default SalesDetailModal;
